import * as classnames from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { StyledLabel } from './StyledLabel';
import {
    inputTheme,
    focusInputTheme,
    errorInputTheme,
    errorFloatingLabelTheme,
    floatingLabelRequiredWrapperTheme
} from '../theme/default-theme';

const InputWrapper = styled.div`
    position: relative;
    display: block;
    width: 100%;
    text-transform: capitalize;

    input {
        box-sizing: border-box;
        border-width: 1px;
        border-style: solid;
        padding: 15px;
        width: 100%;
        height: 52px;
        ${inputTheme};
    }

    input:focus {
        ${focusInputTheme};
    }

    input.elc-floating-label-input-errorField {
        ${errorInputTheme};
    }

    &.elc-floating-label-input-error label.elc-floating-label-string {
        ${errorFloatingLabelTheme};
    }

    .elc-floating-label__required-wrapper {
        ${floatingLabelRequiredWrapperTheme}
    }
`;

const TextWrapper = styled.span``;
const RequiredWrapper = styled.span``;

export interface IFloatingLabelInputProps extends React.HTMLProps<{}> {
    inputLabel: string;
    inputValue?: string;
    inputType?: string;
    className?: string;
    customLabelClassName?: string;
    markAsError?: boolean;
    isRequired?: boolean;
    onBlurHandler?(name: string): void;
    onFocusHandler?(name: string): void;
    moveUp?: boolean;
}

export interface IFloatingLabelInputState {
    isFocused: boolean;
}

export class FloatingLabelInput extends React.Component<
    IFloatingLabelInputProps,
    IFloatingLabelInputState
> {
    constructor(props: IFloatingLabelInputProps) {
        super(props);
        this.handleInputChange = this.handleInputChange.bind(this);
        this.handleInputFocus = this.handleInputFocus.bind(this);
        this.handleInputBlur = this.handleInputBlur.bind(this);

        this.state = {
            isFocused: false
        };
    }

    private handleInputChange(val: React.ChangeEvent<HTMLInputElement>, ...args: unknown[]): void {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const defaultOnChange: any = (this.props.children as React.ReactElement).props.onChange;
        if (typeof defaultOnChange === 'function') {
            defaultOnChange(val, ...args);
        }
    }

    private handleInputBlur(event: React.FocusEvent<HTMLInputElement>): void {
        const defaultOnBlur: (event: React.FocusEvent<HTMLInputElement>) => void = (this.props
            .children as React.ReactElement).props.onBlur;

        if (typeof defaultOnBlur === 'function') {
            defaultOnBlur(event);
        }

        if (this.props.onBlurHandler) {
            this.props.onBlurHandler(event.currentTarget.name);
        }
        this.setState({
            isFocused: false
        });
    }

    private handleInputFocus(event: React.FocusEvent<HTMLInputElement>): void {
        const defaultOnFocus: (event: React.FocusEvent<HTMLInputElement>) => void = (this.props
            .children as React.ReactElement).props.onFocus;

        if (typeof defaultOnFocus === 'function') {
            defaultOnFocus(event);
        }

        if (this.props.onFocusHandler) {
            this.props.onFocusHandler(event.currentTarget.name);
        }
        this.setState({
            isFocused: true
        });
    }

    public render() {
        const classname = classnames(
            'elc-floating-label-input',
            'js-floating-label-input',
            this.props.className,
            {
                'elc-floating-label-input-error': this.props.markAsError,
                'elc-floating-label-input-required': this.props.isRequired,
                'js-floating-label-input-required': this.props.isRequired
            }
        );

        const inputClassName = classnames({
            'elc-floating-label-input-errorField': this.props.markAsError,
            'elc-floating-label-input-field': true,
            'js-floating-label-input-field': true
        });

        const childrenClassName: string =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.props.children as any).props.className || '';
        const childrenDisabled: boolean =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.props.children as any).props.disabled === true;
        const childrenReadOnly: boolean =
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (this.props.children as any).props.readOnly === true;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const input: any = React.cloneElement(this.props.children as React.ReactElement, {
            onFocus: this.handleInputFocus,
            onBlur: this.handleInputBlur,
            onChange: this.handleInputChange,
            className: `${childrenClassName} ${inputClassName}`,
            'aria-disabled': childrenDisabled
        });
        const inputPlaceHolder: string = React.isValidElement(input)
            ? (input as React.ReactElement).props.placeholder
            : '';
        const readOnly: boolean = childrenReadOnly || childrenDisabled;
        const moveLabelUp =
            this.props.moveUp ||
            this.state.isFocused ||
            this.props.inputType === 'date' ||
            !!this.props.inputValue ||
            !!inputPlaceHolder;

        const inputId: string = (this.props.children as React.ReactElement).props.id;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const elementTarget: string | any = (this.props.children as any).type.target;
        const elementType =
            elementTarget && elementTarget.hasOwnProperty('name')
                ? elementTarget.name
                : elementTarget;

        const labelWrapperClassName = classnames(
            this.props.customLabelClassName,
            'elc-floating-label__label-wrapper',
            'js-floating-label__label-wrapper',
            {
                'elc-floating-label__label-is-down': !moveLabelUp
            }
        );
        const textWrapperClassName = classnames(
            'elc-floating-label__text-wrapper',
            'js-floating-label__text-wrapper'
        );
        const requiredWrapperClassName = classnames(
            'elc-floating-label__required-wrapper',
            'js-floating-label__required-wrapper'
        );

        return (
            <InputWrapper className={classname}>
                {input}
                <StyledLabel
                    elementType={elementType}
                    inputId={inputId}
                    moveLabelUp={moveLabelUp}
                    readOnly={readOnly}
                    className={labelWrapperClassName}
                    markAsError={this.props.markAsError}
                >
                    <TextWrapper className={textWrapperClassName}>
                        {this.props.inputLabel}
                    </TextWrapper>
                    {this.props.isRequired && (
                        <RequiredWrapper className={requiredWrapperClassName} />
                    )}
                </StyledLabel>
            </InputWrapper>
        );
    }
}
